import Base64js from 'base64-js'
import { mapGetters } from 'vuex'
import EMenuTags from '~/modules/tags/components/e-menu-tags'
import edsIitCheckboxDialog from '~/modules/eds-iit-checkbox/mixins/edsIitCheckboxDialog'
import contentDialog from '~/mixins/dialogs/contentDialog'
import auth from '~/modules/auth/auth'
import User from '~/models/administration/User'
import mobileDrawer from '~/modules/mobile-drawer/mixins/mobileDrawer'
import informationSnackbar from '~/modules/snackbar/mixins/informationSnackbar'
import documentFieldsFormDialog from '~/modules/documents/mixins/documentFieldsFormDialog'
import confirmationDialog from '~/mixins/dialogs/confirmationDialog'
import successSnackbar from '~/modules/snackbar/mixins/successSnackbar'
import documentSidebar from '~/modules/documents/mixins/documentSidebar'
import informationDialog from '~/mixins/dialogs/informationDialog'
import responsive from '~/mixins/pages/responsive'
import { PARTICIPANTS_ROLES } from '~/modules/documents/constants/participants'

const actions = {
  mixins: [
    mobileDrawer,
    documentFieldsFormDialog,
    edsIitCheckboxDialog,
    confirmationDialog,
    contentDialog,
    informationSnackbar,
    successSnackbar,
    documentSidebar,
    informationDialog,
    responsive
  ],
  computed: {
    ...mapGetters('templatefields', {
      filledFields: 'filledFields'
    }),
    ...mapGetters('organizations', {
      organizations: 'items'
    }),
    ...mapGetters('documents', {
      documentScale: 'documentScale'
    }),
    actions() {
      return [
        {
          name: 'sign',
          text: 'Sign',
          icon: 'pen',
          visible: document =>
            this._.get(document, 'currentUser.role') === PARTICIPANTS_ROLES.OWNER ||
            this._.get(document, 'currentUser.role') === PARTICIPANTS_ROLES.SIGNER,
          call: (document, isTableAction) => this.signDocument(document, isTableAction)
        },
        {
          name: 'share',
          text: 'Share',
          icon: 'file-send-1',
          visible: document => this._.get(document, 'canSend') && this.$User,
          call: (document, isTableAction) => {
            if (this.$hasProVersion && isTableAction) {
              return this.shareDocumentPro(document, isTableAction)
            } else {
              return this.shareDocument(document, isTableAction)
            }
          }
        },
        {
          name: 'changeSendLaterDate',
          text: this.$t('Change send date'),
          icon: 'calendar-edit',
          visible: document => this._.get(document, 'scheduledAt') && this.$User,
          call: document => this.setDelayedSend(document)
        },
        {
          name: 'sendNow',
          text: this.$t('Send now'),
          icon: 'send-smooth',
          visible: document => this._.get(document, 'scheduledAt') && this.$User,
          call: (document, isTableAction) =>
            this.sendDocument(document, { scheduledAt: null }, isTableAction)
        },
        {
          name: 'removeScheduledSend',
          text: this.$t('Cancel send'),
          icon: 'circle-cross',
          visible: document => this._.get(document, 'scheduledAt') && this.$User,
          call: document => this.revokeSendLater(document)
        },
        {
          name: 'convertToPdf',
          text: 'Convert to PDF',
          icon: 'convert',
          // TODO: move ['doc', 'docx'] to const!
          visible: document =>
            ['doc', 'docx'].includes(this._.get(document, 'fileExtension')) && this.$User,
          call: (document, isTableAction) => this.convertDocxToPdf(document, isTableAction)
        },
        {
          name: 'archive',
          text: 'Move to archive',
          icon: 'file-archive',
          visible: document => !this._.get(document, 'archived') && this.$User,
          call: (document, isTableAction) => this.archive(document, isTableAction)
        },
        {
          name: 'unarchive',
          text: 'Move to documents',
          icon: 'document',
          visible: document => this._.get(document, 'archived') && this.$User,
          call: (document, isTableAction) => this.unarchive(document, isTableAction)
        },
        // {
        //   name: 'documents-quick-fill',
        //   text: 'Quick fill',
        //   icon: 'write',
        //   visible: document => this._.get(document, 'canFillFields') && !this.isWidget,
        //   call: document => this.fillFieldsQuick(document)
        // },
        {
          name: 'download',
          text: 'Download',
          icon: 'document-download',
          call: document => this.downloadDocument(document)
        },
        {
          name: 'cancel',
          text: 'Cancel document',
          icon: 'note-remove',
          visible: document => this._.get(document, 'canCancel') && this.$User,
          call: document => this.cancelDocument(document)
        },
        {
          name: 'addtag',
          text: 'Add tag',
          icon: 'tag',
          component: EMenuTags,
          componentProps: document => ({
            document,
            customActivator: true,
            offsetX: true,
            offsetY: false
          })
        },
        {
          name: 'moveToFolder',
          text: 'Move to folder',
          icon: 'folder-arrow-right',
          visible: () => this.$hasProVersion,
          call: document => this.moveDocumentToFolder(document)
        },
        {
          name: 'delete',
          text: 'Delete',
          icon: 'danger-trash',
          visible: document => this._.get(document, 'canDelete') && this.$User,
          call: (document, isTableAction) => this.deleteDocument(document, isTableAction)
        }
      ]
    },
    secondaryActions() {
      const onlySecondaryActions = [
        {
          name: 'documentInfoToggle',
          text:
            this.documentSidebar?.params?.component === 'm-document-info' &&
            this.documentSidebar?.isOpened
              ? 'Hide sidebar'
              : 'Show sidebar',
          icon:
            this.documentSidebar?.params?.component === 'm-document-info' &&
            this.documentSidebar?.isOpened
              ? 'sidebar-left-opened'
              : 'sidebar-left',
          visible: () => this.$hasProVersion,
          call: document => this.showDocumentInfo(document)
        },
        {
          name: 'commentsToggle',
          text: 'Show comments',
          icon: document =>
            document?.unresolvedCommentCount ? 'message-text-new' : 'message-text',
          visible: document => document?.canComment && !this.isWidget,
          call: document => this.showDocumentComments(document)
        }
      ]
      // TODO: change approach of grouping actions
      const secondaryActionNames = [
        'convertToPdf',
        'documents-quick-fill',
        'download',
        'archive',
        'unarchive',
        'delete'
      ]
      const secondaryActions = this._.filter(this.actions, action =>
        secondaryActionNames.includes(action.name)
      )
      return this._.concat(onlySecondaryActions, secondaryActions)
    },
    mobileActions() {
      const onlyMobileActions = [
        {
          name: 'rename',
          text: 'Rename',
          icon: 'pen-edit',
          visible: document =>
            this._.get(document, 'currentUser.role') === PARTICIPANTS_ROLES.OWNER,
          call: document => this.rename(document)
        },
        {
          name: 'signatures',
          text: this.$User ? 'Signatures and access' : 'Signatures',
          icon: 'info-mark',
          call: document => this.showParticipants(document)
        }
      ]
      // TODO: change approach of grouping actions
      const secondaryActionNames = ['download', 'archive', 'unarchive', 'documents-quick-fill']
      const secondaryActions = this._.filter(this.actions, action =>
        secondaryActionNames.includes(action.name)
      )
      return this._.concat(onlyMobileActions, secondaryActions, this.additionalActions)
    },
    additionalActions() {
      return [
        {
          name: 'eventLog',
          text: 'Event log',
          icon: 'clock',
          visible: () => this.$User && this.$hasProVersion,
          call: document => this.showDocumentEventLog(document)
        },
        {
          name: 'cancel',
          text: 'Cancel document',
          icon: 'note-remove',
          visible: document => this._.get(document, 'canCancel') && this.$User,
          call: document => this.cancelDocument(document)
        }
      ]
    }
  },
  methods: {
    async shareDocument(document, isTableAction) {
      window.dataLayer &&
        window.dataLayer.push({
          event: 'click_share',
          email: this._.get(this.$User, 'email')
        })
      let hasSentInvites
      if (this.xsDevice) {
        hasSentInvites = this.mobileDrawer.open({
          title: 'Share this document',
          component: 'm-document-share',
          componentProps: {
            document
          }
        })
      } else {
        hasSentInvites = await this.contentDialog.open({
          width: '724px',
          component: 'm-document-share',
          componentProps: {
            document
          },
          removePadding: true
        })
      }

      if (isTableAction && hasSentInvites) {
        await this.$store.dispatch('documents/read', { id: document.id })
      }
    },
    async shareDocumentPro(document, isTableAction) {
      window.dataLayer &&
        window.dataLayer.push({
          event: 'click_share',
          email: this._.get(this.$User, 'email')
        })
      let hasSentInvites
      if (this.xsDevice) {
        hasSentInvites = this.mobileDrawer.open({
          title: 'Share this document',
          component: 'm-document-share-pro',
          componentProps: {
            document
          }
        })
      } else {
        hasSentInvites = await this.contentDialog.open({
          width: '724px',
          component: 'm-document-share-pro',
          removePadding: true,
          hideCloseButton: true,
          componentProps: {
            document
          }
        })
      }

      if (isTableAction && hasSentInvites) {
        await this.$store.dispatch('documents/getAll')
      }
    },
    async archive(document, isTableAction) {
      try {
        this.archiveLoading = true
        await this.$store.dispatch('documents/toArchive', document.id)
        if (!isTableAction) {
          await this.$store.dispatch('documents/read', { id: document.id })
        } else {
          await this.$store.dispatch('documents/getAll')
        }
        this.$notification.success(this.$t('Document has successfully archived'))
        window.dataLayer &&
          window.dataLayer.push({
            event: 'document_archived',
            email: this._.get(this.$User, 'email')
          })
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.archiveLoading = false
      }
    },
    async unarchive(document, isTableAction) {
      try {
        this.unarchiveLoading = true
        await this.$store.dispatch('documents/fromArchive', document.id)
        if (!isTableAction) {
          await this.$store.dispatch('documents/read', { id: document.id })
        } else {
          await this.$store.dispatch('documents/getAll')
        }
        this.$notification.success(this.$t('Document has successfully unarchived'))
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.unarchiveLoading = false
      }
    },
    downloadDocument(document) {
      this.contentDialog.open({
        width: '512px',
        component: 'block-documents-download',
        componentProps: {
          documentId: document.id
        }
      })
    },
    async signDocument(document, isTableAction = false) {
      window.dataLayer &&
        window.dataLayer.push({
          event: 'click_sign',
          email: this._.get(this.$User, 'email')
        })
      const signOptions = this._.get(this.$route, 'query.signOptions', null) ? JSON.parse(this._.get(this.$route, 'query.signOptions')) : this._.get(document, 'allowedSignatureMethods')
      return await this.useEdsIitCheckboxDialog({
        type: 'edsIitCheckboxCommon',
        method: 'open',
        keyCheck: false,
        pluginTitles: {
          options: this.$t('signPlugin.signDocument')
        },
        dialogParams: {
          visibleSignOptions: signOptions
        },
        onConfirm: async ({ dialogClosed, edsKeyData, isDiia, isMono, sign, isStamp }) => {
          if (dialogClosed) {
            return
          } else if (!edsKeyData?.keyData && !isDiia && !isMono) {
            this.$handlers.error(
              'We could not read the key data. Please try again or choose a different key',
              this
            )
            return
          }
          try {
            const filesToSign = []
            let file = Base64js.toByteArray(this._.get(document, 'file', ''))
            let signedFile
            if (isDiia || isMono) {
              filesToSign.push({
                name: this._.get(document, 'title'),
                content: file,
                isHashed: true,
                externalId: this._.get(document, 'id')
              })
              signedFile = await sign(filesToSign, { keyCheck: false })
              if (!signedFile || !signedFile.length) {
                return
              }
            } else {
              if (document.signatureType === 'internal' && isStamp) {
                const updatedDocument = await this.$store.dispatch('documents/read', {
                  id: document.id
                })
                file = Base64js.toByteArray(this._.get(updatedDocument, 'file', ''))
              }
              signedFile = await sign(file, {
                keyCheck: false,
                title: 'Підпис файлу: ' + document.title
              })
              if (!signedFile) {
                return
              }
            }
            await this.$store.dispatch('documents/sign', {
              id: document.id,
              payload: {
                signature: isDiia || isMono ? this._.get(signedFile[0], 'signature') : signedFile,
                isStamp
              }
            })
            await this.$store.dispatch('documents/read', { id: document.id })
            if (!isTableAction) {
              await this.$store.dispatch('signatures/getAll', { documentId: document.id })
              const showOnboardingPopup =
                this._.get(document, 'currentUser.role') === PARTICIPANTS_ROLES.SIGNER &&
                !this._.get(document, 'currentUser.registered') &&
                !auth.getAuthToken() &&
                !this.isWidget
              const cancellationAct = this._.get(document, 'cancellationAct')
              if (showOnboardingPopup && !cancellationAct) {
                this.callToRegister()
              }
              if (cancellationAct) {
                this.cancellationActSignSuccess()
              }
              if (this.isWidget) {
                window.parent.postMessage('signed', '*')
              }
              if (!this.isWidget && !cancellationAct) {
                this.$notification.success(this.$t('Document has been signed successfully'))
              }
            } else {
              this.$notification.success(this.$t('Document has been signed successfully'))
            }
            // SET FIRST ORG BY SIGNATURE INSTEAD OF USER
            if (
              !this.organizations?.length &&
              !this.$router.currentRoute.query?.token &&
              auth.getAuthToken()
            ) {
              await this.$store.dispatch('organizations/getAll', { page: 1 })
              const firstOrganization = this._.get(this.organizations, '[0]')
              this.$store.commit('organizations/SET_CURRENT_ORGANIZATION', firstOrganization)
              await User.api().setCurrentOrganization({
                organizationId: this._.get(firstOrganization, 'id')
              })
            }
            window.dataLayer &&
              window.dataLayer.push({
                event: 'document_signed',
                email: this._.get(this.$User, 'email')
              })
            return true
          } catch (e) {
            this.$handlers.error(e, this)
            return false
          }
        }
      })
    },
    // TODO: refactor this
    async signDocumentWithDynamicFields(document) {
      try {
        const updatedDocument = await this.fillDynamicFields(document)
        return this.signDocument(updatedDocument)
      } catch (e) {
        this.$handlers.error(e, this)
        return false
      }
    },
    // TODO: do not need updatedDocument ?
    async shareDocumentWithDynamicFields(document) {
      try {
        const updatedDocument = await this.fillDynamicFields(document)
        if (!updatedDocument) {
          return
        }
        await this.shareDocument(updatedDocument)
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    async deleteDocument(document, isTableAction) {
      await this.confirmationDialog.open({
        title: this.$t('Delete document'),
        text: this.$t('Document will be deleted. Are you sure you want to delete it?'),
        buttons: {
          approve: {
            text: this.$t('Delete')
          },
          dismiss: {
            text: this.$t('Cancel')
          }
        },
        icon: {
          src: require('~/assets/images/warning.png'),
          maxWidth: 80
        },
        onConfirm: async () => {
          await this.$store.dispatch('documents/delete', {
            id: document.id
          })
          await this.successSnackbar.open({
            text: this.$t('Document has been deleted successfully')
          })
          if (!isTableAction) {
            this.$router.push('/documents')
          }
        }
      })
    },
    rename(document) {
      this.mobileDrawer.open({
        title: 'Rename',
        component: 'form-document-rename',
        componentProps: {
          document
        }
      })
    },
    showParticipants(document) {
      this.mobileDrawer.open({
        title: 'Signatures and access',
        component: 'form-document-signatures',
        componentProps: {
          document
        }
      })
    },
    // TODO: maybe move to other mixin
    async callToRegister() {
      await this.contentDialog.open({
        width: '512px',
        component: 'block-documents-success',
        componentProps: {
          title: this.$t('Document has been signed'),
          text: this.$t(
            'The sender will receive a message that you have signed the document. To start working with documents in the Dubidoc service, we recommend creating an account, it takes 30 seconds.'
          ),
          buttonText: this.$t('Create an account'),
          onSubmit: () => this.$router.push('/auth?utm_source=viral&utm_medium=modal')
        }
      })
    },
    async cancellationActSignSuccess() {
      await this.informationDialog.open({
        image: require('~/assets/images/check-mark.gif'),
        title: this.$t('The document is signed for cancellation'),
        text: this.$t(
          'All participants who have already signed the document will withdraw notification of its cancellation. As soon as you finish your actions with the document, you immediately receive notifications by mail'
        ),
        width: '512px',
        buttonText: this.$t('Understand')
      })
    },
    async rejectCancellationActSuccess() {
      await this.informationDialog.open({
        image: require('~/assets/images/check-mark.gif'),
        title: this.$t('Cancellation rejected'),
        text: this.$t(
          'The sender will receive a notification that you have rejected the cancellation. The document remains valid and active.'
        ),
        width: '512px',
        buttonText: this.$t('Understand')
      })
    },
    async fillDynamicFields(document) {
      try {
        const payload = {
          fields: this.filledFields,
          scale: this.documentScale
        }
        await this.$store.dispatch('documents/submitFieldValues', { id: document.id, payload })
        this.$store.commit('templatefields/RESET_FILLED_FIELDS')
        const updatedDocument = await this.$store.dispatch('documents/read', { id: document.id })

        const updatedFields = this._.get(updatedDocument, 'fields', [])
        this._.forEach(updatedFields, field => {
          this.$store.commit('templatefields/UPDATE_FIELD', {
            id: field.extraData.id,
            updatedData: {
              documentFieldValue: field.value,
              documentFieldPreValue: field.preValue
            }
          })
        })
        this.$store.commit('documents/SET_SHOULD_RERENDER_DOCUMENT', true)
        return updatedDocument
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    async fillFieldsQuick(document) {
      const fields = this._.get(document, 'fields')
      const formattedFields = this._.map(fields, field => ({
        ...field.extraData,
        documentFieldId: field.id,
        documentFieldValue: field.value,
        documentFieldPreValue: field.preValue
      }))
      const filteredFields = this._.filter(formattedFields, field => !field.documentFieldValue)
      await this.documentFieldsFormDialog.open({
        fields: filteredFields
      })
    },
    async convertDocxToPdf(document) {
      try {
        await this.$store.dispatch('documents/convertToPdf', document.id)
        await this.$store.dispatch('documents/read', { id: document.id })
        await this.informationSnackbar.open({
          icon: 'convert-white',
          text: this.$t('Document has converted to PDF')
        })
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    showDocumentInfo(document) {
      this.documentSidebar.toggle({
        component: 'm-document-info',
        componentProps: {
          document
        }
      })
    },
    async sendDocument(document, payload = {}, isTableAction) {
      try {
        this.sendLoading = true
        await this.$store.dispatch('documents/send', { id: document.id, payload: payload || {} })
        if (!isTableAction) {
          await this.$store.dispatch('documents/read', { id: document.id })
        } else {
          await this.$store.dispatch('documents/getAll')
        }
        return true
      } catch (e) {
        this.$handlers.error(e, this)
        return false
      } finally {
        this.sendLoading = false
      }
    },
    moveDocumentToFolder(document) {
      this.contentDialog.open({
        component: 'm-folders-treeview',
        componentProps: {
          documents: [document] // TODO: possible change
        }
      })
    },
    showDocumentComments(document) {
      this.documentSidebar.toggle({
        component: 'm-document-comments',
        componentProps: {
          document
        },
        onOpenRequest: async document => {
          await this.$store.dispatch('comments/getCommentsByDocumentId', {
            documentId: document.id,
            params: {
              offset: 1,
              limit: 100,
              resolved: false
            }
          })
        }
      })
    },
    showDocumentEventLog(document) {
      this.documentSidebar.toggle({
        component: 'm-document-event-log',
        componentProps: {
          document
        }
      })
    },
    async rejectDocument(document) {
      await this.contentDialog.open({
        width: '512px',
        component: 'form-document-reject-confirm',
        componentProps: {
          document
        }
      })
    },
    cancelDocument(document) {
      this.contentDialog.open({
        width: '512px',
        component: 'm-document-cancel',
        componentProps: {
          documentId: document.id
        }
      })
    },
    rejectCancellationDocument(document) {
      this.contentDialog.open({
        width: '512px',
        component: 'm-document-cancel',
        componentProps: {
          documentId: document.id,
          isReject: true
        }
      })
    },
    async openDiaDialog({ handler, field }) {
      const authData = await this.useEdsIitCheckboxDialog({
        type: 'edsIitCheckboxCommon',
        method: 'open',
        keyCheck: false,
        pluginTitles: {
          diiaRead: 'Authorize via Diia'
        },
        dialogParams: {
          authorizeViaDiia: true
        }
      })
      if (authData) {
        await this.informationDialog.open({
          image: require('~/assets/images/check-mark.gif'),
          title: this.$t('Data obtained from Diia'),
          text: 'Всі поля були заповнено офіційно верифікованими даними', // todo: change
          width: '512px',
          buttonText: this.$t('Go to document')
        })
        handler(field === 'fullName' ? authData.subjFullName : authData.identity) // todo: extend if more than 2 options available
      }
    },
    setDelayedSend(document) {
      this.contentDialog.open({
        width: '512px',
        component: 'form-document-send-later',
        componentProps: {
          document
        }
      })
    },
    async revokeSendLater(document) {
      await this.confirmationDialog.open({
        title: this.$t('Cancel send'),
        text: this.$t(
          'The document will not be sent automatically. You can send it manually later'
        ),
        buttons: {
          approve: {
            text: this.$t('Yes, cancel')
          },
          dismiss: {
            text: this.$t('Come back')
          }
        },
        icon: {
          src: require('~/assets/images/warning.png'),
          maxWidth: 80
        },
        onConfirm: async () => {
          await this.$store.dispatch('documents/update', {
            id: document.id,
            payload: { scheduledAt: null }
          })
          await this.$store.dispatch('documents/read', { id: document.id })
          await this.informationSnackbar.open({
            icon: 'warning-white',
            text: this.$t('Shipment cancelled. Can be sent manually')
          })
        }
      })
    }
  }
}

export default actions
